import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, Award, CheckCircle2, Wrench, Briefcase, GraduationCap, FolderGit2 } from "lucide-react";
import api from "@/lib/api";
import { Roadmap } from "./InternshipLanding";
import { inr } from "./InternshipLayout";

const List = ({ icon: Icon, title, items }) => (
  <div className="bg-white border border-border rounded-2xl p-6"><h3 className="font-heading font-bold text-navy flex items-center gap-2 mb-4"><Icon size={18} className="text-amber" />{title}</h3>
    <ul className="space-y-2">{items.map((x) => <li key={x} className="flex gap-2 text-sm"><CheckCircle2 size={16} className="text-amber shrink-0 mt-0.5" />{x}</li>)}</ul></div>
);

export default function InternshipDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/internship/domains/${slug}`).then(({ data }) => { setD(data); document.title = `${data.title} | VIRIKS Internship`; }).catch(() => setErr(true));
  }, [slug]);
  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="domain-not-found"><h1 className="font-heading text-2xl font-bold">Internship not found</h1><Link to="/internship" className="underline mt-4 inline-block">Browse all internships</Link></div>;
  if (!d) return <div className="py-24 text-center text-muted-foreground">Loading...</div>;
  const phases = [["Phase 1 · Week 1–4", "Foundation", d.roadmap.phase1], ["Phase 2 · Week 5–8", "Practical Development", d.roadmap.phase2], ["Phase 3 · Week 9–12", "Project & Professional Readiness", d.roadmap.phase3]];
  const apply = <Link to={`/internship/apply?domain=${d.slug}`} className="block text-center rounded-full viriks-amber text-white font-semibold py-3.5" data-testid="detail-apply">Apply Now</Link>;
  return (
    <main data-testid="internship-detail">
      <section className="relative bg-[#111111] text-white overflow-hidden">
        <img src={d.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <Link to="/internship#explore" className="text-sm text-white/60 hover:text-white">← All internships</Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-black mt-4" data-testid="detail-title">{d.title}</h1>
          <p className="mt-3 text-white/75 max-w-2xl">{d.short}</p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm"><span className="inline-flex gap-2"><Clock size={16} className="text-amber" />3 Months (12 Weeks)</span><span className="inline-flex gap-2"><Award size={16} className="text-amber" />Completion certificate</span></div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div className="space-y-6">
          <div className="bg-white border border-border rounded-2xl p-6"><h2 className="font-heading font-bold text-navy text-xl mb-2">Program overview</h2><p className="text-muted-foreground">{d.overview}</p></div>
          <div className="grid md:grid-cols-2 gap-6">
            <List icon={GraduationCap} title="What you will learn" items={d.learn} />
            <List icon={Wrench} title="Tools & technologies" items={d.tools} />
            <List icon={CheckCircle2} title="Skills you will develop" items={d.skills} />
            <List icon={FolderGit2} title="Practical assignments" items={d.assignments} />
          </div>
          <div><h2 className="font-heading font-bold text-navy text-xl mb-4">Weekly learning roadmap</h2><Roadmap phases={phases} /></div>
          <div className="bg-white border border-border rounded-2xl p-6"><h3 className="font-heading font-bold text-navy mb-2">Project expectations</h3><p className="text-sm text-muted-foreground">{d.project}</p></div>
          <div className="grid md:grid-cols-2 gap-6"><List icon={GraduationCap} title="Eligibility" items={d.eligible} /><List icon={Briefcase} title="Career opportunities" items={d.careers} /></div>
        </div>
        <aside className="lg:sticky lg:top-24 bg-white border border-border rounded-2xl p-6 space-y-3" data-testid="detail-fee-card">
          <div className="text-sm text-muted-foreground">Program fee</div>
          <div className="font-heading text-4xl font-black text-navy">{inr(d.fees.fee)}/-</div>
          <div className="text-sm text-muted-foreground">+ GST {d.fees.gst_percent}% ({inr(d.fees.gst)}) • Total {inr(d.fees.total)}</div>
          <div className="text-sm">Duration: 3 Months • Certificate on verified completion</div>
          {apply}
        </aside>
      </div>
    </main>
  );
}
